import { useMemo } from "react"
import { useLiveQuery } from "dexie-react-hooks"
import type { ChatSession } from "@stockline/shared/types/ai/chat-session"
import { CHAT_NOTIFICATION_TYPE } from "@stockline/shared/types/ai/chat-session"
import { offlineDb } from "../../../lib/offline/db"
import { CHAT_SESSION_TABLE } from "../../../lib/offline/resources/ai/chat-session/db"
import { dashboardIdsOf } from "./useBiNotifications"

/**
 * Reactive set of dashboard ids that have at least one UNREAD Business-
 * Intelligence brief (`notificationType === "bi"` && `unread === true`),
 * excluding soft-deleted rows. A brief spanning several dashboards lights each.
 *
 * Reads the Dexie chat-session store DIRECTLY (like `useUnreadChatCount`, the AI
 * nav badge) rather than through the AISync reactive provider, so it is safe to
 * call from the app-wide sidebar where that provider is not guaranteed to be
 * mounted; it self-defaults to an empty set when the store isn't ready. Same
 * underlying table and attribution helper as `useBiNotifications`, so the nav
 * badge stays consistent with the per-tab dots.
 */
export function useUnreadBiDashboardIds(): Set<string> {
  const sessions = useLiveQuery(
    () =>
      offlineDb
        .table<ChatSession>(CHAT_SESSION_TABLE)
        .filter(
          (s) =>
            s.unread === true &&
            !s.deletedAt &&
            s.notificationType === CHAT_NOTIFICATION_TYPE.BI
        )
        .toArray(),
    [],
    [] as ChatSession[]
  )
  return useMemo(() => {
    const ids = new Set<string>()
    for (const s of sessions ?? []) {
      for (const id of dashboardIdsOf(s)) ids.add(id)
    }
    return ids
  }, [sessions])
}
